/**
 * Canvas renderer for the "I'm running <EVENT>" share card.
 * Pure drawing — no React. Uses the site's Cabinet Grotesk / JetBrains Mono fonts.
 */
export const FORMATS = {
  post: { label: "POST · 4:5", w: 1080, h: 1350 },
  story: { label: "STORY · 9:16", w: 1080, h: 1920 },
};

const ORANGE = "#EA580C";
const BG = "#09090b";
const DISPLAY = '"Cabinet Grotesk", "Barlow Condensed", Impact, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, monospace';

export async function drawShareCard(canvas, { event, name, city, format, siteUrl }) {
  const { w, h } = FORMATS[format] || FORMATS.post;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");

  try {
    await Promise.all([
      document.fonts.load(`900 200px ${DISPLAY}`),
      document.fonts.load(`800 100px ${DISPLAY}`),
      document.fonts.load(`500 30px ${MONO}`),
    ]);
  } catch { /* fall back to system fonts */ }

  const pad = 84;
  const title = (event.title || "").toUpperCase();
  const distances = (event.distances || []).filter(Boolean).join("  ·  ");
  const isStory = format === "story";

  // Background + soft orange glow
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, w, h);
  const glow = ctx.createRadialGradient(w * 0.15, h * 0.2, 0, w * 0.15, h * 0.2, w * 0.9);
  glow.addColorStop(0, "rgba(234,88,12,0.28)");
  glow.addColorStop(1, "rgba(234,88,12,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);

  // Ghost distance numeral (bottom-right)
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.035)";
  ctx.font = `900 ${isStory ? 720 : 560}px ${DISPLAY}`;
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  ctx.fillText((event.distances?.[0] || "5K").split(" ")[0], w + 30, h - 40);
  ctx.restore();

  // Left accent bar
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, 0, 18, h);

  // Header row
  ctx.fillStyle = "#a1a1aa";
  ctx.font = `500 26px ${MONO}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  spaced(ctx, "ODISHA STEEL CITY RUNNERS", pad, pad, 8);
  ctx.textAlign = "right";
  ctx.fillStyle = ORANGE;
  ctx.font = `900 30px ${DISPLAY}`;
  ctx.fillText("★ OSCR", w - pad, pad - 4);

  // Vertical layout anchor
  let y = isStory ? 400 : 250;

  // "I'M RUNNING"
  ctx.textAlign = "left";
  ctx.fillStyle = ORANGE;
  ctx.font = `900 64px ${DISPLAY}`;
  ctx.fillText("I'M RUNNING", pad, y);
  y += 90;

  // Event title — auto-fit to width
  ctx.fillStyle = "#ffffff";
  const titleSize = fitFont(ctx, title, w - pad * 2, isStory ? 260 : 220, 90, (s) => `900 ${s}px ${DISPLAY}`);
  y = wrapText(ctx, title, pad, y, w - pad * 2, titleSize * 0.88);

  // Distances
  if (distances) {
    y += 20;
    ctx.fillStyle = "#fb923c";
    ctx.font = `800 34px ${DISPLAY}`;
    ctx.fillText(distances, pad, y);
    y += 60;
  }

  // Rule
  y += 30;
  ctx.fillStyle = "#27272a";
  ctx.fillRect(pad, y, w - pad * 2, 2);
  y += 50;

  // Runner name block
  const runner = (name || "").trim().toUpperCase() || "YOUR NAME HERE";
  ctx.fillStyle = "#71717a";
  ctx.font = `500 22px ${MONO}`;
  spaced(ctx, "RUNNER", pad, y, 8);
  y += 40;
  ctx.fillStyle = "#ffffff";
  const nameSize = fitFont(ctx, runner, w - pad * 2, 96, 48, (s) => `800 ${s}px ${DISPLAY}`);
  ctx.font = `800 ${nameSize}px ${DISPLAY}`;
  ctx.fillText(runner, pad, y);
  y += nameSize + 14;
  if ((city || "").trim()) {
    ctx.fillStyle = ORANGE;
    ctx.font = `500 24px ${MONO}`;
    spaced(ctx, city.trim().toUpperCase(), pad, y, 6);
    y += 40;
  }

  // Event meta (date / time / venue) — pinned toward bottom
  const metaTop = h - (isStory ? 580 : 470);
  const dateStr = event.date
    ? new Date(`${event.date}T05:30:00+05:30`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).toUpperCase()
    : "DATE TO BE ANNOUNCED";
  const metaLines = [
    ["DATE", dateStr + (event.time ? `  ·  ${event.time.toUpperCase()}` : "")],
    ["VENUE", (event.location || "ODISHA").toUpperCase()],
  ];
  let my = metaTop;
  for (const [k, v] of metaLines) {
    ctx.fillStyle = "#71717a";
    ctx.font = `500 20px ${MONO}`;
    spaced(ctx, k, pad, my, 8);
    my += 32;
    ctx.fillStyle = "#e4e4e7";
    const vs = fitFont(ctx, v, w - pad * 2, 44, 26, (s) => `800 ${s}px ${DISPLAY}`);
    ctx.font = `800 ${vs}px ${DISPLAY}`;
    ctx.fillText(v, pad, my);
    my += vs + 34;
  }

  // CTA box
  const ctaH = 120;
  const ctaY = h - pad - ctaH;
  ctx.fillStyle = ORANGE;
  ctx.fillRect(pad, ctaY, w - pad * 2, ctaH);
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 40px ${DISPLAY}`;
  ctx.fillText("JOIN ME  →", pad + 36, ctaY + 22);
  ctx.font = `600 26px ${MONO}`;
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.fillText((siteUrl || "odishasteelcityrunner.com").replace(/^www\./, ""), pad + 36, ctaY + 74);
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.font = `500 22px ${MONO}`;
  ctx.fillText(`#OSCR  #${title.replace(/[^A-Z0-9]/g, "")}`, w - pad - 36, ctaY + 76);
  ctx.textAlign = "left";
}

/** Letter-spaced uppercase label. */
function spaced(ctx, text, x, y, spacing) {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
}

/** Shrinks font size from `max` down to `min` until text fits `maxWidth`. Sets ctx.font and returns size. */
function fitFont(ctx, text, maxWidth, max, min, fontFor) {
  let size = max;
  ctx.font = fontFor(size);
  while (size > min && ctx.measureText(text).width > maxWidth) {
    size -= 4;
    ctx.font = fontFor(size);
  }
  return size;
}

/** Word-wraps text using current ctx.font. Returns y after the last line. */
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(/\s+/);
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      y += lineHeight;
      line = word;
    } else {
      line = test;
    }
  }
  if (line) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }
  return y;
}
